import React from "react";
import InputField from "components/fields/InputField";
import { FcGoogle } from "react-icons/fc";
import Checkbox from "components/checkbox";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "context/AuthContext";

export default function SignIn() {
  const { login, verifyLoginCode, resendLoginCode } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from =
    location.state?.from && location.state.from !== "/auth/sign-in"
      ? location.state.from
      : "/admin/default";

  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [error, setError] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);

  // Paso de código (2FA por correo).
  const [step, setStep] = React.useState("credentials"); // "credentials" | "code"
  const [challenge, setChallenge] = React.useState(null);
  const [code, setCode] = React.useState("");
  const [info, setInfo] = React.useState("");
  const [nowTs, setNowTs] = React.useState(() => Date.now());

  const passwordUpdated = location.state?.passwordUpdated === true;

  // Reloj de 1s solo mientras estamos pidiendo el código (para el cooldown de reenvío).
  React.useEffect(() => {
    if (step !== "code") return undefined;
    const t = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(t);
  }, [step]);

  const resendAvailableAt = challenge?.resend_available_at
    ? new Date(challenge.resend_available_at).getTime()
    : 0;
  const resendRemaining = Math.max(0, Math.ceil((resendAvailableAt - nowTs) / 1000));
  const canResend = step === "code" && resendRemaining <= 0 && !submitting;

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setInfo("");
    setSubmitting(true);
    try {
      const result = await login(email.trim(), password);
      if (result?.mfaRequired) {
        setChallenge(result.challenge);
        setStep("code");
        setCode("");
        setInfo(
          `Te enviamos un código de verificación a ${
            result.challenge?.email_hint || "tu correo"
          }.`,
        );
        return;
      }
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo iniciar sesión.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleVerify(e) {
    e.preventDefault();
    if (!challenge?.challenge_id) return;
    setError("");
    setInfo("");
    setSubmitting(true);
    try {
      await verifyLoginCode(challenge.challenge_id, code.trim());
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo verificar el código.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResend() {
    if (!challenge?.challenge_id || !canResend) return;
    setError("");
    setInfo("");
    setSubmitting(true);
    try {
      const next = await resendLoginCode(challenge.challenge_id);
      if (next) setChallenge(next);
      setInfo("Te reenviamos un código nuevo. Revisa tu correo.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo reenviar el código.");
    } finally {
      setSubmitting(false);
    }
  }

  function backToCredentials() {
    setStep("credentials");
    setChallenge(null);
    setCode("");
    setError("");
    setInfo("");
  }

  return (
    <div className="mt-16 mb-16 flex h-full w-full items-center justify-center px-2 md:mx-0 md:px-0 lg:mb-10 lg:items-center lg:justify-start">
      <div className="mt-[10vh] w-full max-w-full flex-col items-center md:pl-4 lg:pl-0 xl:max-w-[420px]">
        <h4 className="mb-2.5 text-4xl font-bold text-navy-700 dark:text-white">
          {step === "code" ? "Verificación" : "Iniciar sesión"}
        </h4>
        <p className="mb-9 ml-1 text-base text-gray-600">
          {step === "code"
            ? "Ingresa el código de 6 dígitos que enviamos a tu correo."
            : "Ingresa tu correo y contraseña para continuar."}
        </p>

        {info ? (
          <p
            className="mb-3 ml-1 rounded-lg border border-emerald-400/50 bg-emerald-50 px-3 py-2 text-sm text-emerald-800 dark:border-emerald-500/35 dark:bg-emerald-950/40 dark:text-emerald-100"
            role="status"
          >
            {info}
          </p>
        ) : null}
        {error ? (
          <p
            className="mb-3 ml-1 rounded-lg border border-red-400/50 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-500/40 dark:bg-red-950/40 dark:text-red-200"
            role="alert"
          >
            {error}
          </p>
        ) : null}

        {step === "code" ? (
          <form onSubmit={handleVerify} className="w-full">
            <InputField
              variant="auth"
              extra="mb-3"
              label="Código de verificación *"
              placeholder="123456"
              id="code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(ev) => setCode(ev.target.value.replace(/\D/g, "").slice(0, 6))}
            />
            <button
              type="submit"
              disabled={submitting || code.trim().length < 6}
              className="linear mt-2 w-full rounded-xl bg-brand-500 py-[12px] text-base font-medium text-white transition duration-200 hover:bg-brand-600 active:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-brand-400 dark:text-white dark:hover:bg-brand-300 dark:active:bg-brand-200"
            >
              {submitting ? "Verificando…" : "Verificar y entrar"}
            </button>

            <div className="mt-4 flex items-center justify-between px-1">
              <button
                type="button"
                onClick={backToCredentials}
                className="text-sm font-medium text-gray-600 hover:text-navy-700 dark:text-gray-400 dark:hover:text-white"
              >
                ← Volver
              </button>
              <button
                type="button"
                onClick={handleResend}
                disabled={!canResend}
                className="text-sm font-medium text-brand-500 hover:text-brand-600 disabled:cursor-not-allowed disabled:opacity-50 dark:text-brand-300"
              >
                {resendRemaining > 0
                  ? `Reenviar código (${resendRemaining}s)`
                  : "Reenviar código"}
              </button>
            </div>
          </form>
        ) : (
          <>
            <div className="mb-6 flex h-[50px] w-full cursor-not-allowed items-center justify-center gap-2 rounded-xl bg-lightPrimary opacity-60 dark:bg-navy-800">
              <div className="rounded-full text-xl">
                <FcGoogle />
              </div>
              <h5 className="text-sm font-medium text-navy-700 dark:text-white">
                Google (próximamente)
              </h5>
            </div>
            <div className="mb-6 flex items-center  gap-3">
              <div className="h-px w-full bg-gray-200 dark:bg-navy-700" />
              <p className="text-base text-gray-600 dark:text-white"> o </p>
              <div className="h-px w-full bg-gray-200 dark:bg-navy-700" />
            </div>

            <form onSubmit={handleSubmit} className="w-full">
              {passwordUpdated ? (
                <p
                  className="mb-3 ml-1 rounded-lg border border-emerald-400/50 bg-emerald-50 px-3 py-2 text-sm text-emerald-800 dark:border-emerald-500/35 dark:bg-emerald-950/40 dark:text-emerald-100"
                  role="status"
                >
                  Contraseña actualizada. Iniciá sesión con la nueva clave.
                </p>
              ) : null}

              <InputField
                variant="auth"
                extra="mb-3"
                label="Correo *"
                placeholder="tu@empresa.cl"
                id="email"
                type="email"
                value={email}
                onChange={(ev) => setEmail(ev.target.value)}
                autoComplete="email"
              />

              <InputField
                variant="auth"
                extra="mb-3"
                label="Contraseña *"
                placeholder="Mín. 8 caracteres"
                id="password"
                type="password"
                value={password}
                onChange={(ev) => setPassword(ev.target.value)}
                autoComplete="current-password"
              />

              <div className="mb-4 flex items-center justify-between px-2">
                <div className="flex items-center">
                  <Checkbox />
                  <p className="ml-2 text-sm font-medium text-navy-700 dark:text-white">
                    Mantener sesión
                  </p>
                </div>
                <Link
                  to="/auth/forgot-password"
                  className="text-sm font-medium text-brand-500 hover:text-brand-600 dark:text-brand-300"
                >
                  ¿Olvidaste tu contraseña?
                </Link>
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="linear mt-2 w-full rounded-xl bg-brand-500 py-[12px] text-base font-medium text-white transition duration-200 hover:bg-brand-600 active:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-brand-400 dark:text-white dark:hover:bg-brand-300 dark:active:bg-brand-200"
              >
                {submitting ? "Entrando…" : "Entrar"}
              </button>
            </form>

            <div className="mt-4">
              <span className=" text-sm font-medium text-navy-700 dark:text-gray-600">
                ¿No tienes cuenta?
              </span>
              <span className="ml-1 text-sm font-medium text-gray-400 dark:text-gray-500">
                Registro vía API /admin (próximamente aquí)
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
